import { useRef, useState, useEffect } from 'react';
import { ArrowLeft, Download, Printer, Send } from 'lucide-react';
import { Proposta, propostaService } from '../../../services/propostaService';
import api from '../../../services/api';

interface EmpresaData {
  nome: string;
  cnpj: string;
  email?: string;
  telefone?: string;
  logradouro?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  logo?: string;
}

interface PropostaPreviewProps {
  proposta: Proposta;
  onBack: () => void;
}

export function PropostaPreview({ proposta, onBack }: PropostaPreviewProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const [empresa, setEmpresa] = useState<EmpresaData | null>(null);

  useEffect(() => {
    loadEmpresa();
  }, []);

  const loadEmpresa = async () => {
    try {
      const response = await api.get('/admin/empresa');
      setEmpresa(response.data);
    } catch (error) {
      console.error('Erro ao carregar empresa:', error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  // Formato do número: 001-12-2025 (numero-mes-ano)
  const formatNumero = () => {
    const num = String(proposta.numero).padStart(3, '0');
    const data = new Date(proposta.dataEmissao);
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    return `${num}-${mes}-${ano}`;
  };

  const handlePrint = () => {
    // Salva o título original
    const originalTitle = document.title;
    // Define o nome do arquivo PDF
    const clienteNome = proposta.client.razaoSocial.replace(/[^a-zA-Z0-9\s]/g, '').trim();
    document.title = `Proposta #${formatNumero()} - ${clienteNome}`;
    
    window.print();
    
    // Restaura o título original após um pequeno delay
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const handleEnviar = async () => {
    if (confirm('Deseja marcar esta proposta como enviada?')) {
      try {
        await propostaService.updateStatus(proposta.id, 'enviada');
        alert('Proposta marcada como enviada!');
      } catch (error) {
        console.error('Erro:', error);
      }
    }
  };

  const empresaNome = empresa?.nome || 'EMPRESA';
  const empresaCnpj = empresa?.cnpj || '00.000.000/0001-00';
  const empresaEmail = empresa?.email || '';
  const empresaTelefone = empresa?.telefone || '';
  const empresaEndereco = empresa ? 
    [empresa.logradouro, empresa.numero, empresa.bairro, empresa.cidade, empresa.estado].filter(Boolean).join(', ') : '';

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between no-print">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <div className="flex gap-2">
          {proposta.status === 'rascunho' && (
            <button onClick={handleEnviar} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Send className="w-4 h-4" /> Marcar como Enviada
            </button>
          )}
          <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
            <Printer className="w-4 h-4" /> Imprimir
          </button>
          <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700">
            <Download className="w-4 h-4" /> Salvar PDF
          </button>
        </div>
      </div>

      {/* Documento */}
      <div ref={printRef} id="proposta-print" className="bg-white border rounded-lg shadow-sm">
        {/* Barra superior azul */}
        <div className="h-2 bg-gradient-to-r from-cyan-600 to-cyan-500"></div>
        
        <div className="p-8">
          {/* Logo centralizada */}
          <div className="text-center mb-8">
            {empresa?.logo ? (
              <img src={empresa.logo} alt="Logo" className="h-12 mx-auto object-contain" />
            ) : (
              <h1 className="text-2xl font-bold text-gray-900">{empresaNome}</h1>
            )}
          </div>

          {/* Cabeçalho: Cliente à esquerda, Proposta à direita */}
          <div className="flex justify-between mb-8">
            {/* Cliente */}
            <div>
              <h2 className="text-sm font-bold text-cyan-600 uppercase tracking-wide mb-2">Cliente</h2>
              <p className="text-lg font-semibold text-gray-900">{proposta.client.razaoSocial}</p>
              {(proposta.client as any).cnpj && (
                <p className="text-sm text-gray-600">CNPJ: {(proposta.client as any).cnpj}</p>
              )}
              {(proposta.client as any).email && (
                <p className="text-sm text-gray-600">{(proposta.client as any).email}</p>
              )}
            </div>

            {/* Dados da Proposta */}
            <div className="text-right">
              <div className="mb-4">
                <p className="text-sm text-gray-600">Proposta <span className="font-semibold text-gray-900">#{formatNumero()}</span></p>
                <p className="text-sm text-gray-600">Emissão <span className="font-semibold text-gray-900">{formatDate(proposta.dataEmissao)}</span></p>
                <p className="text-sm text-gray-600">Validade <span className="font-semibold text-gray-900">{formatDate(proposta.dataValidade)}</span></p>
              </div>
              <h2 className="text-3xl font-bold text-cyan-600">PROPOSTA</h2>
            </div>
          </div>

          {/* Tabela de Itens */}
          <div className="mb-8">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-cyan-600">
                  <th className="py-3 text-left text-sm font-bold text-cyan-600 uppercase w-12">Nº</th>
                  <th className="py-3 text-left text-sm font-bold text-cyan-600 uppercase">Descrição</th>
                  <th className="py-3 text-center text-sm font-bold text-cyan-600 uppercase w-20">Qtd</th>
                  <th className="py-3 text-right text-sm font-bold text-cyan-600 uppercase w-28">Valor Unit.</th>
                  <th className="py-3 text-right text-sm font-bold text-cyan-600 uppercase w-28">Total</th>
                </tr>
              </thead>
              <tbody>
                {proposta.itens.map((item, index) => (
                  <tr key={index} className={`border-b border-gray-200 ${index % 2 === 1 ? 'bg-gray-50' : ''}`}>
                    <td className="py-3 text-sm text-gray-900 text-center">{index + 1}</td>
                    <td className="py-3 text-sm text-gray-900">{item.descricao}</td>
                    <td className="py-3 text-sm text-gray-900 text-center">{Number(item.quantidade)}</td>
                    <td className="py-3 text-sm text-gray-900 text-right">{formatCurrency(Number(item.valorUnit))}</td>
                    <td className="py-3 text-sm font-medium text-gray-900 text-right">{formatCurrency(Number(item.subtotal))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>


          {/* Totais e Termos lado a lado */}
          <div className="flex justify-between mb-8">
            {/* Termos */}
            <div className="max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 italic mb-2">Termos & Condições</h3>
              {proposta.condicoesPagto && (
                <p className="text-sm text-gray-600 mb-2">{proposta.condicoesPagto}</p>
              )}
              {proposta.observacoes && (
                <p className="text-sm text-gray-600">{proposta.observacoes}</p>
              )}
              {!proposta.condicoesPagto && !proposta.observacoes && (
                <p className="text-sm text-gray-600">Proposta válida até a data de validade indicada.</p>
              )}
            </div>

            {/* Totais */}
            <div className="w-64">
              <div className="flex justify-between py-2">
                <span className="text-sm text-gray-600">Subtotal</span>
                <span className="text-sm font-medium text-gray-900">{formatCurrency(Number(proposta.subtotal))}</span>
              </div>
              {Number(proposta.desconto) > 0 && (
                <div className="flex justify-between py-2">
                  <span className="text-sm text-gray-600">Desconto ({Number(proposta.desconto)}%)</span>
                  <span className="text-sm font-medium text-gray-900">- {formatCurrency(Number(proposta.valorDesconto))}</span>
                </div>
              )}
              <div className="flex items-center mt-2">
                <div className="bg-cyan-600 text-white px-4 py-2 font-bold text-sm">TOTAL</div>
                <div className="bg-cyan-50 border border-cyan-600 px-4 py-2 flex-1 text-right">
                  <span className="text-lg font-bold text-cyan-700">{formatCurrency(Number(proposta.valorTotal))}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Linha divisória */}
          <div className="border-t-2 border-cyan-600 mt-8"></div>

          {/* Dados da empresa e Agradecimento lado a lado */}
          <div className="flex justify-between items-start mt-6 mb-8">
            {/* Dados da empresa à esquerda */}
            <div className="text-sm text-gray-700">
              <p className="font-semibold text-cyan-700">{empresaNome}</p>
              {empresaEmail && <p>{empresaEmail}</p>}
              {empresaEndereco && <p>{empresaEndereco}</p>}
              {empresaTelefone && <p>Tel: {empresaTelefone}</p>}
              <p className="text-gray-500">CNPJ: {empresaCnpj}</p>
            </div>

            {/* Mensagem de agradecimento à direita */}
            <div className="text-right">
              <p className="text-2xl font-light text-cyan-600 italic">Obrigado pela preferência!</p>
            </div>
          </div>

          {/* Assinaturas */}
          <div className="grid grid-cols-2 gap-16 mt-8 pt-4">
            <div className="text-center">
              <div className="border-t-2 border-gray-400 pt-2 mx-8">
                <p className="text-sm font-semibold text-gray-900">{empresaNome}</p>
                <p className="text-xs text-gray-500">Fornecedor</p>
              </div>
            </div>
            <div className="text-center">
              <div className="border-t-2 border-gray-400 pt-2 mx-8">
                <p className="text-sm font-semibold text-gray-900">{proposta.client.razaoSocial}</p>
                <p className="text-xs text-gray-500">Cliente</p>
              </div>
            </div>
          </div>
        </div>

        {/* Barra inferior azul */}
        <div className="h-10 bg-cyan-600 flex items-center justify-center mt-4">
          <p className="text-white text-sm font-medium">{empresaNome} • {empresaCnpj}</p>
        </div>
      </div>

      {/* Estilos de impressão */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          
          #proposta-print, #proposta-print * {
            visibility: visible;
          }
          
          #proposta-print {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white !important;
            border: none !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          
          .no-print {
            display: none !important;
          }
          
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          @page {
            margin: 0.5cm;
            size: A4;
          }
        }
      `}</style>
    </div>
  );
}
